import {
    createCardClickedEvent,
    isPublicationCardItemWithVideo,
    PublicationCardItem,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { CommonCardProps } from '../../cards/CardItem.Props'
import {
    getLandscapeWrapperTag,
    StyledCardHeadlineSkeleton,
    StyledInlineWrapper,
    StyledLandscapeLink,
} from '../../cards/Landscape/Landscape.styled'
import { ResponsivePictureSizes } from '../../content/Picture/responsive'
import { MediaMode } from '../CardMedia/CardMedia'
import { ListCardTopicTag } from '../ListCard/components/ListCardTopicTag'
import { LandscapeImageSize } from '../Landscape/Landscape'
import {
    StyledClassificationLabel,
    StyledHeader,
    StyledHeadline,
    StyledLandscapeNumber,
    StyledNightlyMedia,
} from './LandscapeNightly.styled'
import { Author, TopicBylineWrapper } from '../TheNightly/components'
import { getFundingType, getEditorialType } from '../../templates'

export interface LandscapeNightlyProps extends CommonCardProps {
    className?: string
    cardNumber: number

    asListElement?: boolean
    hasBackground?: boolean
    hasPadding?: boolean
    hideByline?: boolean
    mediaMode?: MediaMode
    topicLevel?: 'parent'
    displayNumber?: boolean

    // Images
    imageWidths?: ResponsivePictureSizes
    imagePosition?: 'left' | 'right'
    imageSize?: LandscapeImageSize
    item: MaybeLoaded<PublicationCardItem>
}

export const LandscapeNightly: React.FC<LandscapeNightlyProps> = ({
    className,
    cardNumber,
    asListElement,
    hasBackground,
    hasPadding,
    hideByline,
    mediaMode,
    topicLevel,
    displayNumber,
    imageWidths,
    imagePosition,
    imageSize,
    disableImageLazyLoad,
    item: cardItem,
    onEvent,
}) => {
    const classificationEnabled = useFeature('story-classification')
    const loadedCardItem = cardItem.loaded ? cardItem.result : undefined

    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!loadedCardItem) {
            e.preventDefault()
            return
        }
        onEvent(
            createCardClickedEvent(
                onEvent,
                loadedCardItem,
                'InternalLandscape',
                'Landscape',
                cardNumber,
            ),
        )
    }

    const showByline = hideByline === false

    // Classifications
    const editorialOrFundingType = loadedCardItem
        ? getEditorialType(loadedCardItem, classificationEnabled) ||
          getFundingType(loadedCardItem, classificationEnabled)
        : undefined

    // Images
    const adjustedImageSize = imageSize || 'normal'
    const setImagePosition = imagePosition || 'left' // Default image position to left so it doesn't break existing cards
    const imageRatio = '4:3'
    const imageRight = imagePosition === 'right'
    const imageLeft = imagePosition !== 'right'

    const StyledLandscape = getLandscapeWrapperTag(asListElement ? 'li' : 'div')

    const fontScale = 1

    const defaultImageWidths = {
        mobile: '80px',
        tablet: '120px',
        desktop: '96px',
        fallbackWidth: 140,
        mode: 'fixed',
    } as ResponsivePictureSizes

    const renderImage = (
        <StyledNightlyMedia
            item={cardItem}
            imageWidths={imageWidths ? imageWidths : defaultImageWidths}
            fixedRatio={imageRatio}
            onEvent={onEvent}
            disableImageLazyLoad={disableImageLazyLoad}
            imagePosition={setImagePosition}
            imageSize={adjustedImageSize}
            mediaMode={mediaMode}
            CSSCrop={{
                default: { width: 1, height: 1 },
            }}
            numbered={displayNumber ?? false}
            isLarge={false}
        />
    )

    const renderClassificationLabel = editorialOrFundingType ? (
        <StyledClassificationLabel type={editorialOrFundingType}>
            {editorialOrFundingType}
        </StyledClassificationLabel>
    ) : (
        <></>
    )

    const renderHeadline = loadedCardItem ? (
        <StyledHeadline
            headlineText={loadedCardItem.shortHeadline}
            showVideoIcon={isPublicationCardItemWithVideo(loadedCardItem)}
            kickerMode={'hidden'}
            publicationKind={loadedCardItem.publicationKind}
            requiredAccess={loadedCardItem.requiredAccess}
            fontScale={fontScale}
        />
    ) : (
        <StyledCardHeadlineSkeleton fontScale={fontScale} count={3} />
    )

    return (
        <StyledLandscape
            className={className}
            data-topic={loadedCardItem && loadedCardItem.primaryTopic.id}
            hasBackground={hasBackground || false}
            hasPadding={hasPadding}
        >
            <StyledLandscapeLink
                to={toLinkWithHint(cardItem)}
                onClick={linkClicked}
                isLoading={!loadedCardItem}
                hasBackground={false}
            >
                {/* If not hiding, and we want image on the left! */}
                {mediaMode === 'hidden' ? null : imageLeft && renderImage}

                {displayNumber && (
                    <StyledLandscapeNumber>{cardNumber}</StyledLandscapeNumber>
                )}

                <StyledHeader>
                    {renderClassificationLabel}
                    {renderHeadline}
                    {cardItem.loaded && (
                        <StyledInlineWrapper>
                            <ListCardTopicTag
                                item={cardItem}
                                level={topicLevel}
                            />
                            {showByline && (
                                <TopicBylineWrapper>
                                    <Author>
                                        {`${Math.ceil(
                                            loadedCardItem?.readTimeMinutes ??
                                                1,
                                        )} min read`}
                                    </Author>
                                </TopicBylineWrapper>
                            )}
                        </StyledInlineWrapper>
                    )}
                </StyledHeader>

                {/* If not hiding  and we want image on the right! */}
                {mediaMode === 'hidden' ? null : imageRight && renderImage}
            </StyledLandscapeLink>
        </StyledLandscape>
    )
}
LandscapeNightly.displayName = 'LandscapeNightly'
